/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Anchor, Card } from '@nexigendigital/nxui'
import { serviceAPI } from 'api/service'

import PasswordLightbox from 'containers/services/lightbox/passwordLightbox'

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function Server() {
    const location = useLocation()
    const id = +location.pathname?.split('/')[2]
    const {
        isLoading,
        isError,
        data: { server, schema } = {
            server: { name: '', ip_address: '', username: '', nameservers: [] },
            schema: { username: '', password: '' }
        }
    } = serviceAPI.endpoints.service.useQueryState(id)
    const [lightbox, setLightbox] = useState({
        status: false,
        template: ''
    })
    const columns = useMemo(() => ([
        {
            Header: 'Server Details',
            accessor: 'details'
        },
        {
            Header: '',
            accessor: 'value'
        },
        {
            Header: '',
            accessor: 'action',
            align: 'right'
        }
    ]), [])
    const serverData = useMemo(() => {
        if (isLoading || isError) {
            return []
        }

        const serverInformation = [
            {
                details: 'Server Name',
                value: server?.name,
                action: ''
            },
            {
                details: 'IP Address',
                value: server?.ip_address,
                action: ''
            },
            {
                details: 'Username',
                value: schema?.username,
                action: ''
            },
            {
                details: 'Password',
                value: schema?.password,
                action: (
                    <Anchor
                        color={'primary'}
                        onClick={() => setLightbox({ status: true, template: 'password' })}
                    >
                        Reset
                    </Anchor>
                )
            }
        ]

        if (server && Array.isArray(server.nameservers)) {
            return [
                ...serverInformation,
                ...server.nameservers.map((nameserver, index) => {
                    return {
                        details: `Nameserver ${index + 1}`,
                        value: nameserver
                    }
                })
            ]
        }

        return serverInformation
    }, [server?.ip_address, server?.name, server?.nameservers, isLoading, isError])

    return (
        <>
            <ServerWrapper
                table={{
                    conditions: {
                        loading: isLoading && 'Loading server information...'
                    },
                    render: {
                        columns: () => columns,
                        data: () => serverData
                    }
                }}
            />
            <PasswordLightbox
                id={id}
                status={lightbox.status && lightbox.template === 'password'}
                close={() => setLightbox({ status: false, template: '' })}
            />
        </>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const ServerWrapper = styled(Card)`
    width: 100%;
`
